import * as React from 'react'
import clsx from 'clsx'
import { SxProps, sx as sxFunc, TypographySxProps } from '../sx'
import styles from './index.module.scss'

export interface SvgIconClasses {
  /** Styles applied to the root element. */
  root: string
}

export interface SvgIconProps
  extends Partial<React.HTMLAttributes<HTMLOrSVGElement>> {
  /**
   * Node passed into the SVG element.
   */
  children: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<SvgIconClasses>
  /**
   * @ignore
   */
  className?: string
  /**
   * The color of the component.
   * You can use the `htmlColor` prop to apply a color attribute to the SVG element.
   * @default inherit
   */
  color?:
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning'
  /**
   * The fontSize applied to the icon. Defaults to inherit.
   * @default 'inherit'
   */
  fontSize?: TypographySxProps['fontSize']
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   * @default 'svg'
   */
  Component?: React.ElementType
  /**
   * Applies a color attribute to the SVG element.
   */
  htmlColor?: string
  /**
   * If `true`, the root node will inherit the custom `component`'s viewBox and the `viewBox`
   * prop will be ignored.
   * Useful when you want to reference a custom `component` and have `SvgIcon` pass that
   * `component`'s viewBox to the root node.
   * @default false
   */
  inheritViewBox?: boolean
  /**
   * The shape-rendering attribute. The behavior of the different options is described on the
   * [MDN Web Docs](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/shape-rendering).
   * If you are having issues with blurry icons you should investigate this prop.
   * @default auto
   */
  shapeRendering?:
    | 'auto'
    | 'optimizeSpeed'
    | 'crispEdges'
    | 'geometricPrecision'
  /**
   * Provides a human-readable title for the element that contains it.
   * https://www.w3.org/TR/SVG-access/#Equivalent
   */
  titleAccess?: string
  /**
   * Allows you to redefine what the coordinates without units mean inside an SVG element.
   * For example, if the SVG element is 500 (width) by 200 (height),
   * and you pass viewBox="0 0 50 20",
   * this means that the coordinates inside the SVG will go from the top left corner (0,0)
   * to bottom right (50,20) and each unit will be worth 10px.
   * @default '0 0 24 24'
   */
  viewBox?: string
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
}

export const SvgIcon = React.forwardRef<HTMLOrSVGElement, SvgIconProps>(
  function SvgIcon(inProps, ref) {
    const {
      children,
      color = 'inherit',
      Component = 'svg',
      htmlColor,
      inheritViewBox = false,
      fontSize = 'inherit',
      titleAccess,
      viewBox = '0 0 24 24',
      className,
      classes,
      sx,
      ...other
    } = inProps
    const more: any = {}
    if (!inheritViewBox) {
      more.viewBox = viewBox
    }

    return (
      <Component
        focusable="false"
        className={clsx(
          [styles.root, classes?.root],
          styles[`iconColor-${color}`],
          styles[`fontSize-${fontSize}`],
          className,
          sxFunc(sx)
        )}
        color={htmlColor}
        aria-hidden={titleAccess ? undefined : true}
        role={titleAccess ? 'img' : undefined}
        ref={ref}
        {...more}
        {...other}
      >
        {children}
        {titleAccess ? <title>{titleAccess}</title> : null}
      </Component>
    )
  }
)
